import * as Sentry from '@sentry/nuxt'
import { VSF_MHJ_MY_STORE_ID } from '~/config/cookie'
import localeConfig from '~/config/locale-config'
import type { FlattenedProduct } from '~/types/Product'
import {
  evaluateCampaignRules,
  parseCampaignRules,
  SITE_CODES,
  type CampaignRuleContext,
  type CampaignSiteCode,
  type ParsedCampaignRule,
  type ParsedCampaignRules,
} from '~/utils/brazeCampaignRules'
import type { LayoutStoresStatus } from '~/composables/useLayoutInit'

type Rules = Exclude<ParsedCampaignRules, { error: string }>

/** The layout store each flag waits on, and how it is named when it cannot be read. */
const STORES = [
  { flag: 'needsCart', store: 'cart' },
  { flag: 'needsWishlist', store: 'wishlist' },
] as const satisfies ReadonlyArray<{ flag: keyof Rules; store: keyof LayoutStoresStatus }>

/** A campaign can be delivered before the state it gates on settles; wait rather than false-negative. */
const READY_TIMEOUT_MS = 2000

/** CA and CA-FR share a site id, so the market code comes from the locale rather than `siteId`. */
const siteCodeFor = (locale: string): CampaignSiteCode | undefined => {
  const config = localeConfig[locale]
  if (!config) return undefined
  if (config.country === 'CA') return config.languageCode === 'fr' ? 'CA-FR' : 'CA'
  return SITE_CODES.find((code) => code === config.country)
}

export type CampaignRuleVerdict = { show: boolean; reason?: string }

/**
 * Gate for the `rules` key on a Braze campaign — an in-app message's extras or a banner's custom
 * properties. See `utils/brazeCampaignRules.ts` for the rule catalogue and
 * `docs/braze-campaign-rules.md` for the CRM-facing syntax.
 *
 * Call this inside a Nuxt context and keep the returned object: `evaluate()` reads only refs
 * captured here, so it stays safe to await from a Braze SDK callback.
 */
export const useBrazeCampaignRules = () => {
  const storesStatus = useLayoutStoresStatus()
  const route = useRoute()
  const basketsStore = useBasketsStore()
  const wishlistStore = useWishlistStore()
  const userStore = useUserStore()
  const productStore = useProductStore('pdp')
  const pageType = usePageContentType().contentType
  // Read per evaluation, not captured: Canada switches between en-CA and fr client-side.
  const { locale } = useNuxtApp().$i18n
  // Cookie rather than the profile: it is set for guests too and needs no fetch.
  const myStoreIdCookie = useCookie(VSF_MHJ_MY_STORE_ID)
  // Per instance, which is once per session for the IAM plugin and once per banner component.
  const warnedUnevaluable = new Set<string>()

  const waitFor = async (ready: () => boolean): Promise<boolean> => {
    if (ready()) return true
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        stop()
        resolve(false)
      }, READY_TIMEOUT_MS)
      const stop = watch(ready, (settled) => {
        if (!settled) return
        clearTimeout(timer)
        stop()
        resolve(true)
      })
    })
  }

  /** Exclude the previous product while a reused PDP swaps its route. */
  const productReady = () => pageType.value !== 'pdp' || !productStore.isFetching

  const productFailed = () => pageType.value === 'pdp' && !productStore.isFetching && !productStore.selectedProduct

  const pdpProduct = (): FlattenedProduct | undefined =>
    pageType.value === 'pdp' && !productStore.isFetching ? (productStore.selectedProduct ?? undefined) : undefined

  const buildContext = (): CampaignRuleContext => ({
    pageType: pageType.value,
    fullPath: route.fullPath,
    site: siteCodeFor(locale.value),
    pdpProduct: pdpProduct(),
    basket: basketsStore.basket,
    wishlist: wishlistStore.wishlist,
    isLoggedIn: userStore.isLoggedIn,
    // Nuxt JSON-parses cookie values, so a store number can arrive as a number.
    preferredStoreId: String(userStore.myStoreId ?? myStoreIdCookie.value ?? '') || undefined,
  })

  const decide = (rules: ParsedCampaignRule[], raw: string): CampaignRuleVerdict =>
    evaluateCampaignRules(rules, buildContext()) ? { show: true } : { show: false, reason: `rules not met: ${raw}` }

  /**
   * A rule reading state that never arrived cannot be judged either way, so it is treated like a
   * misconfigured rule: suppress, and warn engineering rather than quietly guessing. Reading an
   * unavailable cart as an empty one would silently invert every `not_cart_*` rule.
   */
  const suppressUnevaluable = (reason: string, raw: string | undefined): CampaignRuleVerdict => {
    // A banner re-renders on every Braze update and on the 2s empty-check, so without this one
    // unevaluable rule warns repeatedly. Sentry's Dedupe only catches consecutive duplicates.
    const key = `${reason}|${raw}`
    if (!warnedUnevaluable.has(key)) {
      warnedUnevaluable.add(key)
      Sentry.captureMessage(`Braze campaign rules could not be evaluated: ${reason}`, {
        level: 'warning',
        extra: { rules: raw },
      })
    }
    return { show: false, reason }
  }

  /** The stores this rule string reads, so a wishlist failure only suppresses wishlist rules. */
  const storesRead = (parsed: Rules) => STORES.filter(({ flag }) => parsed[flag]).map(({ store }) => store)

  const decideUnlessFailed = (parsed: Rules, raw: string) => {
    const failed = storesRead(parsed).find((store) => storesStatus.value[store] === 'failed')
    if (failed) return suppressUnevaluable(`${failed} state failed to load`, raw)
    if (parsed.needsWishlistProducts && wishlistStore.productsFailed) {
      return suppressUnevaluable('wishlist products failed to load', raw)
    }
    if (parsed.needsProduct && productFailed()) return suppressUnevaluable('product failed to load', raw)
    return decide(parsed.rules, raw)
  }

  /**
   * Returns synchronously unless a rule reads cart or wishlist state or a PDP product that hasn't
   * settled yet, so a campaign with no rules is handled with its original timing.
   *
   * Suppression is silent for the customer — the message or banner is simply never shown.
   */
  const evaluate = (raw: string | undefined): CampaignRuleVerdict | Promise<CampaignRuleVerdict> => {
    const parsed = parseCampaignRules(raw)

    if ('error' in parsed) {
      // Fail closed: a typo suppresses the campaign instead of showing it everywhere.
      Sentry.captureMessage(`Braze campaign rules misconfigured: ${parsed.error}`, {
        level: 'warning',
        extra: { rules: raw },
      })
      return { show: false, reason: `invalid rules — ${parsed.error}` }
    }

    if (!parsed.rules.length) return { show: true }

    const storesPending = storesRead(parsed).filter((store) => storesStatus.value[store] === 'pending')
    const productPending = parsed.needsProduct && !productReady()
    if (!storesPending.length && !productPending) return decideUnlessFailed(parsed, raw)

    return Promise.all([
      Promise.all(storesPending.map((store) => waitFor(() => storesStatus.value[store] !== 'pending'))),
      productPending ? waitFor(productReady) : true,
    ]).then(([stores, product]) => {
      const late = storesPending.find((_store, index) => !stores[index])
      if (late) return suppressUnevaluable(`${late} state did not load in time`, raw)
      if (!product) return suppressUnevaluable('product did not load in time', raw)
      return decideUnlessFailed(parsed, raw)
    })
  }

  /** Always-async form for callers that don't need the synchronous fast path (e.g. the debug page). */
  const evaluateAsync = async (raw: string | undefined): Promise<CampaignRuleVerdict> => evaluate(raw)

  return { evaluate, evaluateAsync }
}
